import { Div } from "@tento-ui/component-builtin";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { $$ } from "@tento-ui/fastdom";
import { BarChartIcon, LonaIcons } from "@tento-ui/ui/icons";
import { Json, type JsonObject, type Option, RowId } from "@tento-lona/sheets";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import {
  type BindCellContext,
  type ContentProviders,
  type RenderPlugin,
  RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { type SheetRow } from "@tento-lona";
import {
  createSeriesRenderer,
  type RendererAttributes,
} from "@tento-data-ui";

/**
 * Shared base for the seven canvas renderer DOM plugins (`canvas.hrule`,
 * `canvas.points`, `canvas.line`, `canvas.bar`,
 * `canvas.regression-curve`, `canvas.regression-band`,
 * `canvas.regression-fill`). Per-id
 * subclasses live in `canvas-{hrule,points,line,bar,regression-curve,
 * regression-band,regression-fill}.ts` — each adds `static spec` + `readonly id` and
 * inherits the rest.
 *
 * Behavior is identical across canvas slot ids: `bindCell` is a
 * no-op (the canvas layout owns rendering); `onBindRow` reads
 * `attributes.type` / `attributes.color` regardless of slot. The
 * split exists so each `(id, role)` lives in its own file.
 */
export abstract class DataRendererPluginBase implements RenderPlugin.Renderer<
  HTMLDivElement,
  SheetLegend.DefaultRow
> {
  abstract readonly id: string;

  readonly role = "renderer";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(BarChartIcon);

  constructor(
    public readonly makeRowOptions: RowOptionsElement.Factory,
    public readonly patchRowAttributes: (
      rowId: RowId,
      attributes: JsonObject,
    ) => void,
  ) {}

  makeCell(): HTMLDivElement {
    return Div.make();
  }

  bindCell(
    _$cell: HTMLDivElement,
    _context: BindCellContext,
  ): void {}

  onBindRow(handle: SheetDelegate, row: SheetRow.Joined.Handle): void {
    const attrs = row.variant?.attributes() ?? null;
    const renderType = Json.string(attrs, "type");
    const color = Json.string(attrs, "color");

    const $prev = handle.staticChild.get(
      "renderer",
      row.id,
    ) as Option<DataRendererHeader>;
    const $header = $prev || DataRendererHeader.make();

    $header.bind({
      type: renderType ?? "unknown",
      color: color ?? "var(--text-ter)",
    });

    const height = row.layoutSpec(handle.state.sheet.timescale).cellHeight;
    $header.bindHeight(height);

    handle.staticChild.reveal("renderer", $header, row.id, {
      fillWidth: true,
    });
  }

  loadData = null;

  /**
   * Per-row canvas-renderer provider. `CanvasLayoutPlugin` walks
   * `row.children()` and calls this to collect the
   * `CanvasSeriesRenderer` set used to paint the chart. Reads the
   * row's `type` / `color` / `id` / `key` / shape-specific attributes
   * and delegates to `createSeriesRenderer` from `@tento-data-ui`.
   */
  readonly contentProviders: ContentProviders = {
    canvas: {
      rendererForRow: (row) => {
        const attrs = row.variant?.attributes() ?? null;
        if (attrs == null || !Json.string(attrs, "type")) return null;
        return createSeriesRenderer(attrsForRenderer(attrs)) ?? null;
      },
    },
  };

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return null;
  }

  onResize(_$cell: HTMLDivElement, _context: BindCellContext): void {}
  onViewportColumnsChanged(): void {}
  onRenderedColumnsWillChange(): void {}

  legendFactory: SheetLegendClass<SheetLegend.DefaultRow> = class {
    static make(): SheetLegend.DefaultRow {
      return SheetLegend.DefaultRow.make();
    }

    static bind(
      $legend: SheetLegend.DefaultRow,
      row: SheetRow.Joined.Handle,
      handle: SheetDelegate,
    ): void {
      $legend.bind(
        row,
        (target) => handle.rebind.revealRowOptionsPopover(row, target),
        handle,
      );
    }

    static computeWidth = SheetLegend.DefaultFactory.computeWidth;
  };
}

function attrsForRenderer(
  attrs: JsonObject,
): RendererAttributes {
  const out: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(attrs)) {
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean" ||
      value === null
    ) {
      out[key] = value;
    }
  }
  return out;
}

/** Types that go with `DataRendererPluginBase`: `Attributes`. */
export namespace DataRendererPluginBase {
  export type Attributes = {
    id?: string;
    type?: string;
    color?: string;
    key?: string;
  };
}

/** The header of a data renderer: its type and color. */
@component({
  name: "std-data-renderer-header",
})
export class DataRendererHeader extends LonaWebComponent {
  #$label: HTMLElement = this.$("label");

  bind(props: { type: string; color: string }): DataRendererHeader {
    this.#$label.textContent = props.type;
    this.#$label.style.color = props.color;
    return this;
  }

  bindHeight(heightPx: number) {
    $$.measure(() => {
      const height = this.offsetHeight;
      $$.mutate(() => {
        if (heightPx === height) return;
        this.style.height = heightPx + "px";
      });
    });
  }

  static $styles: Style.Compat[] = [
    css`
      #root {
        align-items: center;
        border-radius: 8px;
        padding: 8px 12px;
        background: var(--bg-surface-light);
        margin-left: 3px;
        width: calc(100% - 6px);
        overflow: hidden;
        height: calc(100% - 2px);
        margin-block: 1px;
      }

      #label {
        color: var(--text-sec);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        align-self: center;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-row id="root">
      <p id="label" class=small></p>
    </std-row>
  `;
}
